import { Injectable } from '@angular/core';
import { ParamMap } from '@angular/router';
import { GeolocationService } from '@wdpr/ra-angular-geolocation';
import { filter, switchMap } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import { from } from 'rxjs';
// DO NOT REMOVE [AnalyticsService] - It is necessary for the framework to load
import { AnalyticsService, ConfigService as AnalyticsConfigService } from '@wdpr/angular-analytics';
// DO NOT REMOVE [AnalyticsService] - It is necessary for the framework to load
import { map } from 'rxjs/operators';
import {forkJoin, Observable } from 'rxjs';

import { ConfigService } from '@finder/core/config.service';
import { Facet } from '@finder/shared/services/filter-emitter-service/facet.class';
import { FinderApiConnectorService } from '@finder/shared/services/finder-api-connector/finder-api-connector.service';
import { WindowRef } from '@finder/shared/services/window-ref/window-ref.service';
import { LocaleService } from '@finder/shared/services/locale-service/locale-service.service';
import finderAnalytics from '@finder/config/analytics.json';
import { FinderPageKeyService } from '@finder/shared/services/finder-page-key/finder-page-key.service';
import { ANALYTICS_FACETS_UTILIZED_ACTION } from '@finder/shared/constants/app.constants';

@Injectable()
export class FinderAnalyticsService {

    private loader: any;
    private loaderPromise: any;
    private resolvePromise: any;
    private guestModel: any;
    private updateModelPromise = new Promise((resolve, reject) => {
        this.resolvePromise = resolve;
    });

    constructor(
        private analyticsService: AnalyticsService,
        private analyticsConfigService: AnalyticsConfigService,
        private pageKeyService: FinderPageKeyService,
        private geolocationService: GeolocationService,
        private config: ConfigService,
        private finderApi: FinderApiConnectorService,
        private windowRef: WindowRef,
        private localeService: LocaleService,
    ) {
        this.useCustomLoader();
    }

    initAnalytics(apiBase: string) {
        // This value sets the path for the analytics JSON files
        // There are also public proerties to set server and other values
        this.analyticsConfigService.serverHost = apiBase;
        this.analyticsConfigService.serverPath = finderAnalytics.config.serverPath;
        this.analyticsConfigService.autoPageViewTrackingTiming = finderAnalytics.config.pageViewTrackTiming;
    }

    /**
     * Gets the analytics file from the API for the given page key
     * NOTE: This is tested in the connector, so no need to test it here.
     */
    /* istanbul ignore next */
    getP13nConfig(): Promise<any> {
        return this.finderApi.getAnalytics(this.pageKeyService.get())
            .toPromise();
    }

    /**
     * Gets guest analytics model data - this is used both by personalization (via
     * the decision attributes loader) as well as the general page analytics
     */
    getGuestModel(): Observable<any> {
        const observables: Observable<any>[] = [ this.geolocationService.getGeoCode({forceRefresh: true}) ];

        return forkJoin(observables)
            .pipe(
                map(res => {
                    const pageData = this.pageKeyService.get() ? this.getPageData() : {} ;

                    // If we've already loaded this up just return the result rather than calling services again
                    /* istanbul ignore next */
                    if (this.guestModel) {
                        this.guestModel.pageUrl = this.windowRef.nativeWindow.location.pathname;

                        return { ...this.guestModel, ...pageData };
                    }

                    const geo = res[0];
                    const hasMedia = res[1] || false;

                    const guestAffiliations = [];

                    // Set geo values
                    if (geo.metro) {
                        geo.dma = geo.metro;
                    }

                    if (geo.metrocode) {
                        geo.dmaCode = geo.metrocode;
                    }

                    if (geo.state) {
                        geo.region = geo.state;
                    }

                    if (geo.zip) {
                        geo.zipcode = geo.zip;
                    }

                    // Check CA resident
                    if (geo.state === finderAnalytics.geo.CA.name) {
                        guestAffiliations.push(finderAnalytics.geo.CA.resident);
                    }

                    // Check FL resident
                    if (geo.state === finderAnalytics.geo.FL.name) {
                        guestAffiliations.push(finderAnalytics.geo.FL.resident);
                    }

                    // Check SoCal resident
                    const zipNumber = Number(geo.zip || '0');
                    if (
                        zipNumber >= finderAnalytics.geo.affiliations.SoCal.zipStart &&
                        zipNumber <= finderAnalytics.geo.affiliations.SoCal.zipEnd
                    ) {
                        guestAffiliations.push(
                            finderAnalytics.geo.affiliations.SoCal.name
                        );
                    }

                    // Get locale parts
                    const localeParts = this.localeService.getLocaleParts();
                    // This allows Analytics to fire on local env when locale parts are missing
                    const resultConfiguration = localeParts ?
                        {
                            contentLanguage: localeParts.language,
                            contentLocale: localeParts.locale,
                        } :
                        { };

                    // We have enough for a basic analytics model
                    const result: any = {
                        configuration: resultConfiguration,
                        geo,
                        guestAffiliations,
                        site: this.config.getValue('siteId'),
                        availableGuestMedia: hasMedia,
                        pageUrl: this.windowRef.nativeWindow.location.pathname,
                    };

                    Object.assign(result, pageData);
                    this.guestModel = result;

                    return result;
                }),
            );
    }

    /**
     * Returns a promise that resolves to analytics model updates.
     * This is used by personalization framework via a factory provider.
     */
    getDecisionAttributes(): Promise<any> {
        return this.getGuestModel()
            .toPromise();
    }

    /**
     * Resolves the promise that returns the analytics configuration data.
     */
    /* istanbul ignore next */
    updateModel() {
        // if loaderPromise exists means
        // finderAnalyticsLoader was called first.
        if (this.loaderPromise) {
            this.loaderPromise.then(this.resolvePromise);
        }
        // after resolve the promise we revert back the
        // ConfigService.load method to the original.
        this.analyticsConfigService.loader = this.loader;
    }

    /**
     * Updates the analytics model with page-specific data
     */
    updatePage(params?: ParamMap) {
        this.analyticsConfigService.addGlobalProcessors(this.getPageData(params));
    }

    updateErrorPage(status) {
        this.analyticsConfigService.addGlobalProcessors({
            pageId: `${status}`,
            siteSection: finderAnalytics.errorPage.siteSection,
        });
    }

    updateOtherProperties(props: any) {
        this.analyticsConfigService.addGlobalProcessors(props);
    }

    /**
     * Updates the analytics model with facility data
     */
    updateFacility(id: number | string, name: string) {
        const processor: any = {
            facility: {
                id,
                name
            }
        };

        this.analyticsConfigService.addGlobalProcessors(processor);
    }

    /**
     * Updates the analytics model with geo and affiliation data
     */
    updateGeoAndAffiliations(guestModel) {
        this.analyticsConfigService.addGlobalProcessors(guestModel);
    }

    updateStoreType() {
        const processor: any = {
            storeType: finderAnalytics.storeType // IMPORTANT - triggers the tracking events
        };

        this.analyticsConfigService.addGlobalProcessors(processor);
    }

    updateAnalyticsModel(
        urlParams: ParamMap,
        guestModel: any,
        entityId?: string,
        title?: string,
        linkId?: string,
        otherParams?: any
    ) {
        this.updateGeoAndAffiliations(guestModel);
        this.updateFacility(entityId, title);
        this.updatePage(urlParams);
        this.updateStoreType();
        if (otherParams) {
            this.updateOtherProperties(otherParams);
        }
        if (linkId) {
            this.updateModelAndTrack({linkId});
        }
        this.updateModel();
    }

    /**
     * It adds the list of facets that are applied into analytics model, as well as the facet passed with its action.
     * For example, if a user selects a filter that has 'parades' as associated facet, parameters will be:
     * facetsList: [
     *     id: 'parades',
     *     ...
     * ]
     * facet: 'parades'
     * If the facet is already on the facetsList, it means the user is removing it, so we are setting 'Remove' as action.
     * facetsUtilized: The facet that is being added or removed. It is equal to prop20/evar13. i.e: 'Add:parades', 'Remove:abc'
     * facetsProgress: The list of all facets that are applied. It is equal to evar14. i.e: 'character-experiences,parades'.
     */
    updateAnalyticsFacets(facetsList: Facet[], facet?: string) {
        // Subscribe to the pageKey service and wait for a value before running this code
        this.pageKeyService.pageKeyReady().pipe(
            // Ignore empty results. The first result from the pageKey service is a blank string
            filter(result => !isEmpty(result)),
            switchMap(result => from(this.analyticsService.getModel()))
        ).subscribe((model: any) => {
            const modelProperties = {
                facetsUtilized: facet ?
                    [{
                        name: facet,
                        action: facetsList.find(element => element.id === facet) ?
                            ANALYTICS_FACETS_UTILIZED_ACTION.add :
                            ANALYTICS_FACETS_UTILIZED_ACTION.remove
                    }] :
                    [],
                facetsProgress: facetsList.map((obj) => ({ name: obj.id }))
            };

            Object.assign(model, modelProperties || {});
            this.analyticsService.update();
        });
    }

    /**
     * It updates the passed properties on the analytics model.
     * i.e: if we want to update the linkId:
     * updateModelAndTrack({linkId: 'someLinkId'})
     * This fires "otherClick" event
     */
    updateModelAndTrack(modelProperties: any) {
        this.analyticsService.trackLink(modelProperties);
    }

    /**
     * Checks if p13n is enabled by brand and locale.
     * Config example format:
     * {
     *     wdw: [ 'en_US' ],
     *     dlr: []
     * }
     */
    /* istanbul ignore next */
    private isP13nEnabled() {
        // Get the config (see above for format)
        const p13n = this.config.getValue('personalizationEnabled');

        // Using the current brand, grab the array of enabled locales (if any)
        const enabledLocales: string[] = p13n[this.config.getValue('siteId')] || [];

        // Check to see if the current locale is in the array of brand-enabled locales
        return enabledLocales.indexOf(this.localeService.getLocale()) > -1;
    }

    /* istanbul ignore next */
    private useCustomLoader() {
        // save the original loader method with the proper binding
        this.loader = this.analyticsConfigService.loader.bind(this.analyticsConfigService);
        // replace the loader method with our wrapper.
        this.analyticsConfigService.loader = this.finderAnalyticsLoader.bind(this);
    }

    /**
     * This method overrides the original `ConfigService.load` method.
     * I will return a promise that will resolves when `FinderAnalyticsService.updateModel`
     * is called.
     */
    /* istanbul ignore next */
    private finderAnalyticsLoader(pageKey: any): Promise<any> {
        // call the original loader method
        // and save the promise.
        this.loaderPromise = this.loader(pageKey);

        // return our promise that will be resolve
        // with updateModel method.
        return this.updateModelPromise;
    }

    /**
     * Gets URL parameter parts from a `ParamMap`
     */
    private getParts(params: ParamMap): any {
        return {
            type: params.get('type') || '',
            parkName: params.get('location') || '', // want to make sure 'null' is not the value
            friendlyId: params.get('id')
        };
    }

    /**
     * Gets a `siteSection` analytics value from the parameter parts
     */
    private getSiteSection(parts): string {
        // If parkName is undefined the template literal will turn it into 'undefined'
        // so we need to explicitly handle that with an empty string - the replace will
        // then strip off the trailing slash after the empty string
        return `${finderAnalytics.siteSectionPrefix || ''}/${parts.type}/${parts.parkName || ''}`
            .replace(/-/g, '')      // dashes get stripped
            .replace(/\/\//g, '/')  // double slashes to single slash
            .replace(/\/$/, '');    // trailing slash gets stripped
    }

    /**
     * Gets the analytics `pageId` value from the `friendlyId` parameter part
     */
    private getPageId(friendlyId): string {
        return friendlyId.replace(/-/g, '');
    }

    /**
     * Gets a variety of analytics data from a `ParamMap`
     */
    private getPageData(params?: ParamMap): any {
        let parts;
        if (params) {
            parts = this.getParts(params);
        } else {
            parts = this.pageKeyService.getParts();
        }
        const pageId = this.getPageId(parts.friendlyId);
        let siteSection = this.getSiteSection(parts);
        // On listing pages we need to remove the pageId from the siteSection
        // to avoid getting i.e. /attractions/attractions on the analytics pageName
        if (siteSection.endsWith('/' + pageId)) {
            siteSection = siteSection.replace('/' + pageId, '');
        }

        // Set the basic page/site values
        return {
            pageId,
            pageVariant: finderAnalytics.pageVariant,
            siteSection,
        };
    }
}
